import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import { Grid } from '@mui/material';
import CircularProgress from '@material-ui/core/CircularProgress';
import DangerousOutlinedIcon from '@mui/icons-material/DangerousOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { makeStyles } from '@material-ui/core/styles';
import { Tooltip, Typography } from '@material-ui/core';

import { DashboardHelpers } from 'utils';
import { useOrganizations, useProperties, useFields, useDevices } from 'hooks';
import { Link } from 'react-router-dom';

import _ from 'lodash';

const useStyles = makeStyles((theme) => ({
  container: {
    minWidth: 400,
    borderLeft: '1px solid #eee',
    padding: '5px',
  },
  chartTitle: {
    marginLeft: 50,
    fontWeight: 300,
    paddingBottom: theme.spacing(1),
  },
  largeIcon: {},
  subtitle: {
    color: '#ccc',
  },
  err: {
    color: '#f00',
  },
  warn: {
    color: '#db0',
  },
  info: {
    color: '#2ab',
  },
  none: {
    color: '#bbb',
  },
}));

const EventsAtAGlance = ({ selectedDevice, selectedPropertyId, selectedFieldId, selectedOrg, events }) => {
  useEffect(() => {
    let helperCounts = {
      counts: { err: 0, warn: 0, info: 0 },
      eventsByType: { error: [], warning: [], info: [] },
      deduplicatedEventsByType: { error: [], warning: [], info: [] },
    };

    const localEvents = Object.assign({}, events);
    const filteredByRead = {};

    _.forEach(localEvents, (layer, key) => {
      const unread = _.filter(layer, (event) => {
        return !event.read_at;
      });
      filteredByRead[key] = unread;
    });

    if (selectedDevice) {
      helperCounts = DashboardHelpers.getEventsAndSubordinates(
        selectedDevice.id,
        'device',
        filteredByRead,
        properties,
        fields,
        devices
      );
      setSelectedLayerId(selectedDevice.id);
      setLayer('device');
    } else if (selectedFieldId) {
      helperCounts = DashboardHelpers.getEventsAndSubordinates(
        selectedFieldId,
        'field',
        filteredByRead,
        properties,
        fields,
        devices
      );
      setSelectedLayerId(selectedFieldId);
      setLayer('field');
    } else if (selectedPropertyId) {
      helperCounts = DashboardHelpers.getEventsAndSubordinates(
        selectedPropertyId,
        'property',
        filteredByRead,
        properties,
        fields,
        devices
      );
      setSelectedLayerId(selectedPropertyId);
      setLayer('property');
    } else if (selectedOrg) {
      helperCounts = DashboardHelpers.getEventsAndSubordinates(
        selectedOrg.id,
        'organization',
        filteredByRead,
        properties,
        fields,
        devices
      );
      setSelectedLayerId(selectedOrg.id);
      setLayer('organization');
    }

    setCounts(helperCounts?.counts);
    setEventsByType(helperCounts?.eventsByType);
    const { deduplicatedEventsByType } = helperCounts;
    const infoPack = { organizations, properties, fields, devices };

    const errorListComponents = DashboardHelpers.getEventGlanceTooltipListByType(
      deduplicatedEventsByType,
      'error',
      infoPack
    );
    const warningListComponents = DashboardHelpers.getEventGlanceTooltipListByType(
      deduplicatedEventsByType,
      'warning',
      infoPack
    );
    const infoListComponents = DashboardHelpers.getEventGlanceTooltipListByType(
      deduplicatedEventsByType,
      'info',
      infoPack
    );

    setErrorListComponents(errorListComponents);
    setWarningListComponents(warningListComponents);
    setInfoListComponents(infoListComponents);
  }, [selectedDevice, selectedPropertyId, selectedFieldId, selectedOrg, events]);

  const { t } = useTranslation();
  const { organizations } = useOrganizations();
  const { properties } = useProperties();
  const { fields } = useFields();
  const { devices } = useDevices();

  const [layer, setLayer] = useState('organization');
  const [activeLayerId, setSelectedLayerId] = useState(selectedOrg ? selectedOrg.id : undefined);
  const [counts, setCounts] = useState({ err: 0, warn: 0, info: 0 });
  const [errorListComponents, setErrorListComponents] = useState([]);
  const [warningListComponents, setWarningListComponents] = useState([]);
  const [infoListComponents, setInfoListComponents] = useState([]);
  const [eventsByType, setEventsByType] = useState({ error: [], warning: [], info: [] });

  const classNames = useStyles();

  const errorDisplay = (
    <div>
      <DangerousOutlinedIcon color="error"></DangerousOutlinedIcon>
      {!!counts && counts.errors > 0 ? (
        <Link
          to={{
            pathname: '/events',
            state: {
              redirectData: {
                layer: layer,
                type: 'error',
                time_start: '',
                time_end: '',
                search: '',
                layerId: activeLayerId,
                skip: 0,
                limit: 0,
                organization_id: selectedOrg?.id,
                events: eventsByType,
              },
            },
          }}
        >
          <Typography variant="h2" className={classNames.errors}>
            {counts.errors}
          </Typography>
        </Link>
      ) : (
        <Typography variant="h2" className={classNames.none}>
          {0}
        </Typography>
      )}
      <Typography variant="subtitle1" className={classNames.subtitle}>
        {t('errors')}
      </Typography>
    </div>
  );

  const warningDisplay = (
    <div>
      <WarningAmberOutlinedIcon color="warning"></WarningAmberOutlinedIcon>
      {!!counts && counts.warnings > 0 ? (
        <Link
          to={{
            pathname: '/events',
            state: {
              redirectData: {
                layer: layer,
                type: 'warning',
                time_start: '',
                time_end: '',
                search: '',
                layerId: activeLayerId,
                skip: 0,
                limit: 0,
                organization_id: selectedOrg?.id,
                events: eventsByType,
              },
            },
          }}
        >
          <Typography variant="h2" className={classNames.errors}>
            {counts.warnings}
          </Typography>
        </Link>
      ) : (
        <Typography variant="h2" className={classNames.none}>
          {0}
        </Typography>
      )}
      <Typography variant="subtitle1" className={classNames.subtitle}>
        {t('warnings')}
      </Typography>
    </div>
  );

  const infoDisplay = (
    <div>
      <InfoOutlinedIcon color="info"></InfoOutlinedIcon>
      {!!counts && counts.info > 0 ? (
        <Link
          to={{
            pathname: '/events',
            state: {
              redirectData: {
                layer: layer,
                type: 'info',
                time_start: '',
                time_end: '',
                search: '',
                layerId: activeLayerId,
                skip: 0,
                limit: 0,
                organization_id: selectedOrg?.id,
                events: eventsByType,
              },
            },
          }}
        >
          <Typography variant="h2" className={classNames.errors}>
            {counts.info}
          </Typography>
        </Link>
      ) : (
        <Typography variant="h2" className={classNames.none}>
          {0}
        </Typography>
      )}
      <Typography variant="subtitle1" className={classNames.subtitle}>
        {t('info')}
      </Typography>
    </div>
  );

  return (
    <Grid item className={classNames.container}>
      <Typography variant="h4">{t(layer)} Events at a Glance</Typography>
      {true && (
        <Grid
          container
          direction="row"
          justifyContent="space-evenly"
          justifyItems="center"
          sx={{ paddingTop: 2 }}
          textAlign="center"
        >
          <Grid item>
            {errorListComponents.length > 0 ? (
              <Tooltip
                title={
                  <React.Fragment>
                    <div>{errorListComponents}</div>
                  </React.Fragment>
                }
              >
                {errorDisplay}
              </Tooltip>
            ) : (
              errorDisplay
            )}
          </Grid>
          <Grid item>
            {warningListComponents.length > 0 ? (
              <Tooltip
                title={
                  <React.Fragment>
                    <div>{warningListComponents}</div>
                  </React.Fragment>
                }
              >
                {warningDisplay}
              </Tooltip>
            ) : (
              warningDisplay
            )}
          </Grid>
          <Grid item>
            {infoListComponents.length > 0 ? (
              <Tooltip
                title={
                  <React.Fragment>
                    <div>{infoListComponents}</div>
                  </React.Fragment>
                }
              >
                {infoDisplay}
              </Tooltip>
            ) : (
              infoDisplay
            )}
          </Grid>
        </Grid>
      )}
      {false && (
        <Grid container justifyContent="center" alignContent="center" sx={{ marginTop: 5 }}>
          <CircularProgress color="secondary" />
        </Grid>
      )}
    </Grid>
  );
};

EventsAtAGlance.propTypes = {
  selectedDevice: PropTypes.object,
  selectedPropertyId: PropTypes.string,
  selectedFieldId: PropTypes.string,
  selectedOrg: PropTypes.object,
  events: PropTypes.object,
  fetchEventsCount: PropTypes.func,
  telemetryPeriod: PropTypes.number,
};

EventsAtAGlance.defaultProps = {};

export default EventsAtAGlance;
